import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  useTheme,
  Radio,
  Alert,
  CardContent,
  Card
} from '@mui/material';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  StripeCardNumberElementOptions,
  StripeCardExpiryElementOptions,
  StripeCardCvcElementOptions,
  StripeCardNumberElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardCvcElementChangeEvent
} from '@stripe/stripe-js';
import { trackPixelEvent, PixelEvents } from '../../utils/meta/metaPixel';
import {
  AuthenticatedFetchError,
  authenticatedFetch,
  readErrorMessage,
} from '../../utils/authenticatedFetch';

export interface PaymentFormUserProps {
  uid: string;
  email: string;
  firstName: string;
  lastName: string | null;
}

export interface PaymentFormProps {
  user: PaymentFormUserProps;
  paymentType: 'monthly' | 'lifetime';
  onComplete: (data: { subscriptionId: string; customerId: string; userId: string }) => void;
  // onBack?: () => void; // Optional: if you want a back button
}

type PaymentFormData = {
  postalCode: string;
};

const PaymentForm: React.FC<PaymentFormProps> = ({ user, paymentType, onComplete }) => {
  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  
  const [formData, setFormData] = useState<PaymentFormData>({
    postalCode: '',
  });
  
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);

  const cardComplete = cardNumberComplete && cardExpiryComplete && cardCvcComplete;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null); // Clear error on input change
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    //console.log("Beginning handleSubmit in PaymentForm.tsx");
    event.preventDefault();
    
    // Prevent multiple submissions
    if (loading || isProcessing) return;
    
    setLoading(true);
    setIsProcessing(true);
    setError(null);

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please wait a moment and try again.');
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      setError('Card details are not available. Please refresh and try again.');
      setLoading(false);
      return;
    }

    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName,
          email: user.email,
          address: {
            postal_code: formData.postalCode.trim(),
          },
        },
      });
      console.log("Finished stripe.createPaymentMethod in PaymentForm.tsx. Payment Method:", paymentMethod);

      if (stripeError) {
        setError(stripeError.message || 'Failed to create payment method. Please check your card details.');
        setLoading(false);
        return;
      }

      if (!paymentMethod) {
        setError('Failed to create payment method. Please try again.');
        setLoading(false);
        return;
      }

      // Track payment info added before sending to backend
      const pixelEvent = paymentType === 'lifetime' 
        ? PixelEvents.INITIATE_STRIPE_CHECKOUT_LIFETIME 
        : PixelEvents.INITIATE_STRIPE_CHECKOUT_MONTHLY;
      
      trackPixelEvent(pixelEvent, {
        currency: 'USD',
        value: paymentType === 'lifetime' ? 120 : 39.99,
      });

      const response = await authenticatedFetch('/api/create-stripe-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          firstName: user.firstName,
          lastName: user.lastName || '',
          paymentType,
        }),
      });

      if (!response.ok) {
        const message = await readErrorMessage(
          response,
          response.status === 401
            ? 'Your session expired. Please sign in again and try paying.'
            : 'Payment failed. Please check your card details and try again.'
        );
        throw new Error(message);
      }

      const responseData = await response.json();

      if (responseData.success) {
        if (paymentType === 'monthly') {
          trackPixelEvent(PixelEvents.START_TRIAL, {
            content_name: 'Monthly Subscription Trial Started',
            content_category: 'SAT Tutoring',
            currency: 'USD',
            value: 39.99,
            predicted_ltv: 39.99 * 3,
          });
        }
        onComplete(responseData);
      } else {
        setError(responseData.error || 'Failed to set up your subscription. Please try again.');
      }
    } catch (err: unknown) {
      console.error('Error processing payment:', err);
      if (err instanceof AuthenticatedFetchError) {
        setError(
          err.status === 401
            ? 'Your session expired. Please sign in again and try paying.'
            : err.message
        );
        return;
      }
      const message =
        err instanceof Error && typeof err.message === 'string'
          ? err.message
          : 'An unexpected error occurred. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
      setIsProcessing(false); // Reset processing state
    }
  };

  // Common styles for Stripe elements (copied from StartFreeTrialRatio.tsx)
  const commonStripeElementStyles = {
    base: {
      fontSize: '16px',
      color: theme.palette.text.primary,
      '::placeholder': {
        color: theme.palette.text.secondary,
      },
    },
    invalid: {
      color: theme.palette.error.main,
      iconColor: theme.palette.error.main,
    },
  };

  const stripeElementWrapperSx = {
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
    borderRadius: 1,
    padding: '16.5px 14px',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      borderColor: theme.palette.text.primary,
    },
    '&.Mui-focused': { // This class might need to be manually added/managed for focus effect
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      padding: '15.5px 13px',
    },
    // Add a class for error state if needed, or rely on Stripe's invalid state for text color
    '&.Mui-error': {
        borderColor: theme.palette.error.main,
    },
  };

  const cardNumberElementOptions: StripeCardNumberElementOptions = {
    style: commonStripeElementStyles,
    showIcon: true,
    iconStyle: 'solid',
  };

  const otherCardElementOptions: StripeCardExpiryElementOptions | StripeCardCvcElementOptions = {
    style: commonStripeElementStyles,
  };

  return (
    <Card elevation={0} sx={{marginTop: 4, width: '100%', maxWidth: '700px', marginX: 'auto' }}>
      <CardContent sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h5" component="h2" gutterBottom textAlign="center" sx={{ mb: 1 }}>
            Secure Payment
          </Typography>
          {/*<Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
            Start your 14-day free trial. Cancel anytime.
          </Typography>*/}

          {/* Display User Info (Read-only) - Optional 
          <Box sx={{ mb: 2, p: 1.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
            <Typography variant="body2">Account: {user.email}</Typography>
            <Typography variant="body2">Name: {user.firstName} {user.lastName}</Typography>
          </Box>*/}

          {/* Card Number */}
          <Typography variant="h6" gutterBottom sx={{ mt: 1, fontWeight: 500 }}>
            Card details
          </Typography>
          <Box sx={{ ...stripeElementWrapperSx, mb: 2 }}>
            <CardNumberElement
              options={cardNumberElementOptions}
              onChange={(e: StripeCardNumberElementChangeEvent) => {
                setCardNumberComplete(e.complete);
                if (e.error) setError(e.error.message); else setError(null);
              }}
            />
          </Box>

          {/* Expiry Date and CVC */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flexBasis: '50%' }}>
              <Typography variant="caption" display="block" gutterBottom sx={{ color: theme.palette.text.secondary}}>
                Expiration date*
              </Typography>
              <Box sx={{ ...stripeElementWrapperSx }}>
                <CardExpiryElement
                  options={otherCardElementOptions as StripeCardExpiryElementOptions}
                  onChange={(e: StripeCardExpiryElementChangeEvent) => {
                    setCardExpiryComplete(e.complete);
                    if (e.error) setError(e.error.message); else setError(null);
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ flexBasis: '50%' }}>
              <Typography variant="caption" display="block" gutterBottom sx={{ color: theme.palette.text.secondary}}>
                CVC*
              </Typography>
              <Box sx={{ ...stripeElementWrapperSx }}>
                <CardCvcElement
                  options={otherCardElementOptions as StripeCardCvcElementOptions}
                  onChange={(e: StripeCardCvcElementChangeEvent) => {
                    setCardCvcComplete(e.complete);
                    if (e.error) setError(e.error.message); else setError(null);
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Postal Code */}
          <TextField
            label="Postal Code"
            name="postalCode"
            value={formData.postalCode}
            onChange={handleInputChange}
            fullWidth
            required
            autoComplete="postal-code"
            sx={{ mb: 2 }}
            disabled={loading}
          />

          {/* Order Details */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="div">
              {paymentType === 'lifetime' ? 'Lifetime Access' : 'Monthly Subscription'}
            </Typography>
            <Typography variant="body2" align="right">
              {paymentType === 'lifetime' ? '$120 one-time' : '$39.99 / month'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading || isProcessing || !stripe || !elements || !cardComplete || !formData.postalCode.trim()}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              mt: 1
            }}
          >
            {loading || isProcessing ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              paymentType === 'lifetime' ? 'Get Lifetime Access - $120' : 'Start Monthly Subscription - $39.99'
            )}
          </Button>

            {/*Add the functionality and note: We'll send a reminder 3 days before your trial ends. */}
          <Typography variant="body2" align="center" sx={{ mt: 2, color: theme.palette.text.secondary }}>
            By clicking "Subscribe", you agree to our Terms and authorize this payment.
          </Typography>
          {/*<Typography variant="body2" align="right" sx={{ mt: 2, color: theme.palette.text.secondary }}>
            Powered by Stripe
          </Typography>*/}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PaymentForm;